import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  useTheme,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { LIST_FAQ_ENTRIES } from '../../graphql/queries';
import { configService } from '../../services/configService';

interface FAQEntry {
  faqId: string;
  category: 'USER' | 'ADMIN';
  question: string;
  answer: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy?: string;
}

const FaqPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [operationManualUrl, setOperationManualUrl] = useState<string>('/docs/sst_operation_manual_ver4.2.pdf');
  const [serviceAgreementUrl, setServiceAgreementUrl] = useState<string>('/docs/sst_service_agreement_v2.pdf');

  // 設定ファイルからURLを取得
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await configService.loadConfig();
        setOperationManualUrl(config.operationManualUrl);
        setServiceAgreementUrl(config.serviceAgreementUrl);
      } catch (error) {
        console.error('設定の読み込みに失敗しました:', error);
        // エラー時はデフォルト値を使用
      }
    };
    loadConfig();
  }, []);

  // 一般ユーザー向けFAQデータを取得
  const { 
    data: userFaqData, 
    loading: userFaqLoading, 
    error: userFaqError 
  } = useQuery(LIST_FAQ_ENTRIES, {
    variables: { category: 'USER', limit: 100 }
  });

  // 管理者向けFAQデータを取得
  const { 
    data: adminFaqData, 
    loading: adminFaqLoading, 
    error: adminFaqError 
  } = useQuery(LIST_FAQ_ENTRIES, {
    variables: { category: 'ADMIN', limit: 100 }
  });

  const generalFaqEntries: FAQEntry[] = userFaqData?.listFAQEntries?.items || [];
  const adminFaqEntries: FAQEntry[] = adminFaqData?.listFAQEntries?.items || [];

  const QAIcon: React.FC<{ type: 'Q' | 'A' }> = ({ type }) => (
    <Box
      sx={{
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        backgroundColor: theme.palette.smartSkillTalk.linkBlue,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#FFFFFF',
        fontWeight: 'bold',
        fontSize: '14px',
        flexShrink: 0,
        marginRight: '10px',
      }}
    >
      {type}
    </Box>
  );

  const QAItem: React.FC<{ item: FAQEntry }> = ({ item }) => (
    <Paper
      sx={{
        backgroundColor: '#F4FAFF',
        borderRadius: '10px',
        padding: '20px',
        marginBottom: '20px',
      }}
    >
      <Box sx={{ display: 'flex', marginBottom: '20px' }}>
        <QAIcon type="Q" />
        <Typography
          sx={{
            fontSize: '14px',
            color: '#000000',
            letterSpacing: '0.1em',
            flex: 1,
            whiteSpace: 'pre-wrap',
          }}
        >
          {item.question}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex' }}>
        <QAIcon type="A" />
        <Typography
          sx={{
            fontSize: '14px',
            color: '#000000',
            letterSpacing: '0.1em',
            flex: 1,
            whiteSpace: 'pre-wrap',
          }}
        >
          {item.answer}
        </Typography>
      </Box>
    </Paper>
  );

  const SectionTitle: React.FC<{ title: string; summary: string | React.ReactNode }> = ({ title, summary }) => (
    <Box sx={{ marginBottom: '20px' }}>
      <Typography
        variant="h2"
        sx={{
          fontSize: '24px',
          fontWeight: 700,
          color: theme.palette.smartSkillTalk.linkBlue,
          marginBottom: '10px',
          letterSpacing: '0.1em',
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: '14px',
          color: '#000000',
          paddingLeft: '20px',
          letterSpacing: '0.1em',
          lineHeight: 1.6,
        }}
      >
        {summary}
      </Typography>
    </Box>
  );

  const DownloadLink: React.FC<{ title: string; onClick?: () => void }> = ({ title, onClick }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        padding: '0 30px 50px',
        cursor: 'pointer',
      }}
      onClick={onClick}
    >
      <PdfIcon
        sx={{
          width: '24px',
          height: '24px',
          color: '#C81824',
          marginRight: '10px',
        }}
      />
      <Typography
        sx={{
          fontSize: '14px',
          color: theme.palette.smartSkillTalk.linkBlue,
          textDecoration: 'underline',
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.8,
          },
        }}
      >
        {title}
      </Typography>
    </Box>
  );

  return (
    <Container 
      maxWidth="lg"
      sx={{
        paddingLeft: 0,
        paddingRight: 0,
      }}
    >
      <Box>
        {/* イントロテキスト */}
        <Typography
          sx={{
            textAlign: 'center',
            fontSize: '14px',
            color: '#000000',
            marginBottom: '30px',
          }}
        >
          ご不明な点やご質問がございましたら、以下の項目をご確認ください。
        </Typography>

        {/* FAQ（よくあるご質問） */}
        <Box sx={{ marginBottom: '50px' }}>
          <SectionTitle
            title="FAQ（よくあるご質問）"
            summary="お問い合わせの前に、まずよくあるご質問をご確認ください。"
          />
          <Box>
            {userFaqLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <CircularProgress />
              </Box>
            ) : userFaqError ? (
              <Alert severity="error">
                一般ユーザー向けFAQの読み込みに失敗しました。
              </Alert>
            ) : (
              generalFaqEntries.map((item) => (
                <QAItem key={item.faqId} item={item} />
              ))
            )}
          </Box>
        </Box>

        {/* FAQ（管理者向け） */}
        <Box sx={{ marginBottom: '50px' }}>
          <SectionTitle
            title="FAQ（管理者向け）"
            summary="企業管理者様向けのよくあるご質問です。"
          />
          <Box sx={{ padding: '0 15px' }}>
            {adminFaqLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <CircularProgress />
              </Box>
            ) : adminFaqError ? (
              <Alert severity="error">
                管理者向けFAQの読み込みに失敗しました。
              </Alert>
            ) : (
              adminFaqEntries.map((item) => (
                <QAItem key={item.faqId} item={item} />
              ))
            )}
          </Box>
        </Box>

        {/* ご利用マニュアル */}
        <Box sx={{ marginBottom: '30px' }}>
          <SectionTitle
            title="ご利用マニュアル"
            summary="本サービスのご利用には、「SmartSkill Talk ご利用マニュアル」のPDFをご確認ください。（ダウンロード）"
          />
          <DownloadLink
            title="SmartSkill Talk ご利用マニュアル"
            onClick={() => {
              // PDFファイルを新しいタブで開く（相対パスまたは外部URLのどちらでも対応）
              window.open(operationManualUrl, '_blank');
            }}
          />
        </Box>

        {/* サービス約款 */}
        <Box sx={{ marginBottom: '30px' }}>
          <SectionTitle
            title="サービス約款"
            summary="本サービスのご利用には、「SmartSkill Talk サービス約款」のPDFをご確認ください。（ダウンロード）"
          />
          <DownloadLink
            title="SmartSkill Talk サービス約款"
            onClick={() => {
              // PDFファイルを新しいタブで開く（相対パスまたは外部URLのどちらでも対応）
              window.open(serviceAgreementUrl, '_blank');
            }}
          />
        </Box>

        {/* 推奨環境 */}
        <Box sx={{ marginBottom: '30px' }}>
          <SectionTitle
            title="推奨環境"
            summary="本サービスを快適にご利用いただくための推奨環境は以下の通りです。"
          />
          <Box sx={{ padding: '0 30px 50px' }}>
            <Typography
              sx={{
                fontSize: '12px',
                color: '#666666',
                marginBottom: '10px',
                letterSpacing: '0.1em',
              }}
            >
              2026/03/26時点
            </Typography>
            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #E0E0E0' }}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        width: '200px', 
                        fontWeight: 'bold', 
                        backgroundColor: '#F5F5F5',
                        borderRight: '1px solid #E0E0E0',
                        fontSize: '14px',
                        letterSpacing: '0.1em',
                      }}
                    >
                      デバイス
                    </TableCell>
                    <TableCell sx={{ fontSize: '14px', letterSpacing: '0.1em' }}>
                      PC / スマートフォン / タブレット
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        fontWeight: 'bold', 
                        backgroundColor: '#F5F5F5',
                        borderRight: '1px solid #E0E0E0',
                        fontSize: '14px',
                        letterSpacing: '0.1em',
                      }}
                    >
                      推奨ブラウザ
                    </TableCell>
                    <TableCell sx={{ fontSize: '14px', letterSpacing: '0.1em' }}>
                      Google Chrome（最新バージョン）
                      <br />
                      Microsoft Edge（最新バージョン）
                      <br />
                      ※webm または m4a の音声コーデックが使用できるブラウザ
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        fontWeight: 'bold', 
                        backgroundColor: '#F5F5F5',
                        borderRight: '1px solid #E0E0E0',
                        fontSize: '14px',
                        letterSpacing: '0.1em',
                      }}
                    >
                      OS
                    </TableCell>
                    <TableCell sx={{ fontSize: '14px', letterSpacing: '0.1em' }}>
                      Windows11 24H2 以降 / macOS 14（Sonoma）以降 / Android 14 以降 / iOS, iPadOS 17 以降
                      <br />
                      <br />
                      ※マイクによる音声入力、および音声出力が可能なデバイスであること
                      <br />
                      ※推奨ブラウザが動作し、OSのプロバイダーによってサポートされているプラットフォーム
                      <br />
                      ※iOSではWebRTCが利用できること、それ以外のデバイスではMediaRecorder APIが利用できること
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        fontWeight: 'bold', 
                        backgroundColor: '#F5F5F5',
                        borderRight: '1px solid #E0E0E0',
                        fontSize: '14px',
                        letterSpacing: '0.1em',
                      }}
                    >
                      インターネット接続
                    </TableCell>
                    <TableCell sx={{ fontSize: '14px', letterSpacing: '0.1em' }}>
                      安定したインターネット接続環境（上下1Mbps以上を推奨）
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>

        {/* お問い合わせフォーム */}
        <Box sx={{ marginBottom: '30px' }}>
          <SectionTitle
            title="お問い合わせフォーム"
            summary={
              <>
                ご質問やご要望がある方は、【お問い合わせフォームへ】ボタンからお問い合わせください。担当より順次ご連絡いたします。
                <br />
                <br />
                ※内容によっては、ご返信までにお時間をいただく場合がございます。
              </>
            }
          />
          <Box sx={{ textAlign: 'center', padding: '10px' }}>
            <Button
              onClick={() => {
                navigate('/help/inquiry');
              }}
              sx={{
                backgroundColor: theme.palette.smartSkillTalk.green,
                color: '#FFFFFF',
                borderRadius: '10px',
                padding: '14px 24px',
                fontSize: '16px',
                height: '52px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#4a9500',
                },
              }}
            >
              お問い合わせフォームへ
            </Button>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default FaqPage;
